import { supabaseService } from '../integrations/supabase/client';
import { userStateService } from './UserStateService';
import { FileItem } from './FilesService';
import { appConfigService } from './AppConfigService';

interface ReportResult {
    success: boolean;
    message: string;
    error?: Error;
}

export class IssueReportingService {
    private static instance: IssueReportingService;

    private constructor() {}

    public static getInstance(): IssueReportingService {
        if (!IssueReportingService.instance) {
            IssueReportingService.instance = new IssueReportingService();
        }
        return IssueReportingService.instance;
    }

    /**
     * Submit a report for a problematic file
     * @param fileItem The file to report issues with
     * @param reason The reason for reporting the issue
     * @param includePdf Whether to include the PDF in the report
     * @returns Report submission result
     */
    public async submitReport(
        fileItem: FileItem, 
        reason: string, 
        includePdf: boolean
    ): Promise<ReportResult> {
        try {
            // Ensure reason doesn't exceed 2000 characters
            const truncatedReason = reason.substring(0, 2000);
            
            // Get current user information from UserStateService
            const userService = userStateService;
            const { name, email } = userService.getState();

            // Create timestamp-based folder structure
            const hhmm = (new Date()
                .toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }))
                .replace(/:/g, 'h');
            const year = new Date().getFullYear();
            const monthPad = new Date().getMonth() + 1 < 10 ? `0${new Date().getMonth() + 1}` : new Date().getMonth() + 1;
            const dayPad = new Date().getDate() < 10 ? `0${new Date().getDate()}` : new Date().getDate();
            const folderPath = `${year}-${monthPad}/${dayPad}/${hhmm}`;
            const bucketName = 'conversion-reports';

            // Upload PDF if requested
            if (includePdf) {
                const pdfBlob = new Blob([fileItem.pdfRawContent], { type: 'application/pdf' });
                const pdfFilename = `${fileItem.pdfFileName}`;
                const result = await supabaseService.uploadBlobToSupabase(
                    pdfBlob,
                    bucketName,
                    `${folderPath}/${pdfFilename}`,
                    'application/pdf'
                );

                if (!result.success) {
                    console.error('PDF Upload failed:', result.error);
                    return {
                        success: false,
                        message: 'Failed to upload PDF file',
                        error: new Error(result.error)
                    };
                }
            }

            // Upload parsed results JSON
            let parsedResultsJson = JSON.parse(fileItem.parsedResults);
            // Sanitize the parsed results before handling them
            parsedResultsJson = Array.isArray(parsedResultsJson) 
                ? parsedResultsJson.map(item => this.sanitizeObject(item))
                : this.sanitizeObject(parsedResultsJson);
            
            const jsonFilename = fileItem.pdfFileName.replace(/\.[^/.]+$/, '.json');
            const result2 = await supabaseService.uploadJsonToSupabase(
                parsedResultsJson,
                bucketName,
                `${folderPath}/${jsonFilename}`
            );

            if (!result2.success) {
                console.error('Parsed Results JSON Upload failed:', result2.error);
                return {
                    success: false,
                    message: 'Failed to upload parsed results',
                    error: new Error(result2.error)
                };
            }

            // Upload raw results
            const rawResults = fileItem.rawResults;
            const rawResultsFilename = fileItem.pdfFileName.replace(/\.[^/.]+$/, '.txt');
            const result3 = await supabaseService.uploadTextToSupabase(
                rawResults,
                bucketName,
                `${folderPath}/${rawResultsFilename}`
            );

            if (!result3.success) {
                console.error('Raw Results Upload failed:', result3.error);
                return {
                    success: false,
                    message: 'Failed to upload raw results',
                    error: new Error(result3.error)
                };
            }

            // Upload metadata
            const metadataJson = {
                fileId: fileItem.fileItemId,
                fileName: fileItem.pdfFileName,
                userChoseToIncludePdf: includePdf,
                reason: truncatedReason,
                reportAsAt: new Date().toISOString(),
                lastProcessedDate: fileItem.lastProcessedDate.toISOString(),
                userName: name || '',
                userEmail: email || ''
            };

            const metadataFilename = fileItem.pdfFileName.replace(/\.[^/.]+$/, '.metadata.json');
            const result4 = await supabaseService.uploadJsonToSupabase(
                metadataJson,
                bucketName,
                `${folderPath}/${metadataFilename}`
            );

            if (!result4.success) {
                console.error('Metadata Upload failed:', result4.error);
                return {
                    success: false,
                    message: 'Failed to upload metadata',
                    error: new Error(result4.error)
                };
            }

            // Send the metadata to the DENO_API_URL's '/issue' endpoint
            try {
                const config = appConfigService.getConfig();
                const denoApiUrl = config.denoApiUrl;
                const issueEndpoint = '/issue';
                const url = `${denoApiUrl}${issueEndpoint}`;

                // Create form data
                const formData = new FormData();
                
                // Automatically add all metadata fields to the formData
                Object.entries(metadataJson).forEach(([key, value]) => {
                    // Convert any value to string to ensure compatibility with FormData
                    formData.append(key, String(value));
                });
                
                // Add file path information
                formData.append('bucketName', bucketName);
                formData.append('folderPath', folderPath);
                
                // Send the request
                const response = await fetch(url, {
                    method: 'POST',
                    body: formData
                });

                if (!response.ok) {
                    console.error('Issue report API call failed:', response.statusText);
                    // We continue despite this error as the data is already saved to Supabase
                    // But we log it for monitoring
                }
            } catch (apiError) {
                console.error('Error sending issue to API:', apiError);
                // We continue despite this error as the data is already saved to Supabase
            }

            return {
                success: true,
                message: 'Report submitted successfully'
            };
        } catch (error) {
            console.error('Error submitting report:', error);
            return {
                success: false,
                message: 'Error submitting report',
                error: error instanceof Error ? error : new Error(String(error))
            };
        }
    }

    // Utility methods for sanitization
    private sanitizeText(text: string): string {
        if (!text) return '';
        const temp = document.createElement('div');
        temp.textContent = text;
        return temp.innerHTML;
    }

    private sanitizeObject(obj: Record<string, any>): Record<string, any> {
        const result: Record<string, any> = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const value = obj[key];
                if (typeof value === 'string') {
                    result[key] = this.sanitizeText(value);
                } else {
                    result[key] = value;
                }
            }
        }
        return result;
    }
}

export const issueReportingService = IssueReportingService.getInstance(); 